import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Pagination,
  Stack,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useCoreProducts from "../../services/useCoreProducts";
import useCoreProductCategories from "../../services/useCoreProductCategories";
import useFarmers from "../../services/useFarmers";
import useDeleteCoreProduct from "../../services/useDeleteCoreProduct";
import { IProductCore } from "../../types/products";

const PAGE_SIZE = 20;

/**
 * Página de listagem do catálogo de produtos (core.Product)
 * Esta é a nova listagem que consome o endpoint /core_products/
 * e é independente do sistema legado.
 */
const CoreProductsList = () => {
  const navigate = useNavigate();

  // Estados para filtros
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [producerFilter, setProducerFilter] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [productToDelete, setProductToDelete] = useState<number | null>(null);

  // Hook de exclusão
  const deleteCoreProductMutation = useDeleteCoreProduct();

  // Preparar parâmetros para o hook
  const filters = {
    page,
    ordering: "product",
    active: activeFilter === "all" ? undefined : activeFilter === "true",
    category: categoryFilter || undefined,
    producer: producerFilter || undefined,
  };

  const { data: products, isLoading, error } = useCoreProducts(filters);
  const { data: categories } = useCoreProductCategories();
  const { data: farmers } = useFarmers();

  useEffect(() => {
    setPage(1);
  }, [activeFilter, categoryFilter, producerFilter]);

  useEffect(() => {
    if (!products) return;
    const totalPages = Math.max(1, Math.ceil(products.count / PAGE_SIZE));
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [products, page]);

  // Ordenar produtos apenas por nome (ordem alfabética)
  const sortedProducts = useMemo(() => {
    if (!products || !products.results) return [];
    return [...products.results].sort((a, b) =>
      a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
    );
  }, [products]);

  const handleActiveFilterChange = (event: SelectChangeEvent) => {
    setActiveFilter(event.target.value);
  };

  const handleCategoryFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setCategoryFilter(value === "" ? null : Number(value));
  };

  const handleProducerFilterChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setProducerFilter(value === "" ? null : Number(value));
  };

  const handleViewDetails = (productId: number) => {
    // Placeholder: navega para rota de detalhes que será implementada depois
    navigate(`/app/core-products/${productId}`);
  };

  const handleEdit = (productId: number) => {
    navigate(`/app/core-products/${productId}/edit`);
  };

  const handleDeleteClick = (productId: number) => {
    setProductToDelete(productId);
    setErrorMessage("");
  };

  const handleDeleteConfirm = () => {
    if (!productToDelete) return;

    deleteCoreProductMutation.mutate(productToDelete, {
      onSuccess: () => {
        setProductToDelete(null);
      },
      onError: (err: any) => {
        console.error("Erro ao excluir produto", err);
        const data = err?.response?.data ?? err;
        if (data && typeof data === "object") {
          const errorMessages = Object.keys(data).map((key) => {
            const value = (data as any)[key];
            const messages = Array.isArray(value) ? value : [value];
            return `${key}: ${messages.join(", ")}`;
          });
          setErrorMessage(errorMessages.join("\n"));
        } else {
          setErrorMessage(
            "Erro ao excluir produto. Verifique se o produto não possui ofertas vinculadas.",
          );
        }
        setProductToDelete(null);
      },
    });
  };

  const handleDeleteCancel = () => {
    setProductToDelete(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Erro ao carregar produtos. Tente novamente.
        </Alert>
      </Container>
    );
  }

  const filteredCategories = categories?.filter(
    (category) => category.products_count && category.products_count > 0,
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1">
          Catálogo de Produtos
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/app/core-products/new")}
        >
          Novo produto
        </Button>
      </Box>

      {errorMessage && (
        <Alert
          severity="error"
          onClose={() => setErrorMessage("")}
          sx={{ mb: 3 }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={handleActiveFilterChange}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="true">Ativos</MenuItem>
                <MenuItem value="false">Inativos</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoryFilter ? categoryFilter.toString() : ""}
                label="Categoria"
                onChange={handleCategoryFilterChange}
              >
                <MenuItem value="">Todas</MenuItem>
                {filteredCategories?.map((category) => (
                  <MenuItem key={category.id} value={category.id.toString()}>
                    {category.name}
                    {category.products_count !== undefined &&
                      ` (${category.products_count})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Produtor</InputLabel>
              <Select
                value={producerFilter ? producerFilter.toString() : ""}
                label="Produtor"
                onChange={handleProducerFilterChange}
              >
                <MenuItem value="">Todos</MenuItem>
                {farmers?.map((farmer) => (
                  <MenuItem key={farmer.id} value={farmer.id.toString()}>
                    {farmer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Lista de produtos */}
      <Card>
        <CardContent>
          {!sortedProducts || sortedProducts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum produto encontrado.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Produtor</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell align="center">Refrigerado</TableCell>
                    <TableCell align="center">Ordem de empilhamento</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProducts.map((product: IProductCore) => (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {product.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{product.producer.name}</TableCell>
                      <TableCell>{product.category?.name || "-"}</TableCell>
                      <TableCell>{product.unit || "-"}</TableCell>
                      <TableCell align="center">
                        {product.refrigerated ? (
                          <Chip label="Sim" size="small" color="info" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Não
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {product.stack_order}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={product.active ? "Ativo" : "Inativo"}
                          size="small"
                          color={product.active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(product.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(product.id)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir produto">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(product.id)}
                            disabled={deleteCoreProductMutation.isLoading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {products && products.count > PAGE_SIZE && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            color="primary"
            page={page}
            count={Math.max(1, Math.ceil(products.count / PAGE_SIZE))}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Stack>
      )}

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={productToDelete !== null} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza de que deseja excluir este produto do catálogo?
          </DialogContentText>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Essa ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteCoreProductMutation.isLoading}
          >
            {deleteCoreProductMutation.isLoading ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CoreProductsList;
